import { createComponent } from "react-fela";
import { useEffect, useState } from "react";

import { colors } from "~/styleguide";

import NoSSR from "~/components/shared/no-ssr";

export function loadHubSpotFormsScript() {
    if (typeof window === "undefined") return Promise.resolve();

    if (window.hbspt && window.hbspt.forms) {
        return Promise.resolve(window.hbspt);
    }

    if (window.__hubspotFormsScriptPromise) {
        return window.__hubspotFormsScriptPromise;
    }

    window.__hubspotFormsScriptPromise = new Promise((resolve, reject) => {
        const existingScript = document.getElementById("hubspot-forms-v2");
        const script = existingScript || document.createElement("script");

        script.id = "hubspot-forms-v2";
        script.src = "https://js.hsforms.net/forms/embed/v2.js";
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.hbspt);
        script.onerror = reject;

        if (!existingScript) {
            document.body.appendChild(script);
        }
    });

    return window.__hubspotFormsScriptPromise;
}

export default function FormWrapper(props) {
    return (
        <NoSSR>
            <HubSpotForm {...props} />
        </NoSSR>
    );
}

function HubSpotForm({ prefix, formId, goToWebinarKey, extend }) {
    if (!formId) return null;

    /**
     * Form container ID Prefix. Pass a `prefix` string prop to help specify where
     * the form should be injected, especially when the same HubSpot form may exist
     * on the same page (re: sign up forms in sidebar and footer)
     */
    const getIdPrefix = () => {
        return prefix ? `${prefix}-` : "";
    };

    const onFormLoaded = () => {
        setIsLoaded(true);
    };

    const [isLoaded, setIsLoaded] = useState(false);
    const computedPrefix = `${getIdPrefix(prefix)}-${formId}`.replace(
        "/",
        "--"
    );

    useEffect(() => {
        if (!window || typeof window === "undefined") return;

        loadHubSpotFormsScript().then(onWindowReady);
    }, []);

    const onWindowReady = () => {
        // Don't load the form twice
        if (isLoaded) return;

        if (typeof window === "undefined" || !window.hbspt) return;

        setIsLoaded(true);

        let params = {
            portalId: 5265893,
            formId: formId,
            target: `#${computedPrefix}`,
            onFormReady: onFormLoaded,
            width: "100%"
        };

        // Add Go TO Webinar key, if set
        // See: https://knowledge.hubspot.com/integrations/use-the-gotowebinar-integration-with-hubspot
        if(goToWebinarKey) {
            params.goToWebinarWebinarKey = goToWebinarKey;
        }

        window.hbspt.forms.create(params);
    };

    return (
        <NoSSR>
            <HubForm extend={extend} id={`${computedPrefix}`}></HubForm>
        </NoSSR>
    );
}

const HubForm = createComponent(
    () => ({
        fontSize: 16,
        // protecting against explicitly set form sizes coming in from HubSpot
        "& iframe": {
            maxWidth: "100% !important"
        },
        "& .hs-form-field": {
            marginBottom: "20px",
            "& label": {
                fontSize: 14
            }
        },
        "& .input input": {
            width: "100%",
            borderRadius: 6,
            backgroundColor: colors.lightGrey,
            position: "relative",
            border: "none",
            lineHeight: "50px",
            height: 50,
            padding: "0px 12px",
            transition: "outline .5s",
            "&:focus": {
                outlineColor: colors.blue
            }
        },
        "& .input input[type='input']": {
            "-webkit-appearance": "none"
        },
        "& .input input[type='email']": {
            "-webkit-appearance": "none"
        },
        "& .input input[type='textarea']": {
            "-webkit-appearance": "none"
        },
        "& textarea": {
            width: "100%",
            maxWidth: "100%",
            marginHeight: 150,
            borderRadius: 6,
            backgroundColor: colors.lightGrey,
            padding: "12px",
            border: "none",
            transition: "outline .5s",
            "&:focus": {
                outlineColor: colors.blue
            }
        },
        "& .hs-form-booleancheckbox": {
            marginTop: 22
        },
        "& .input input[type='checkbox']": {
            width: "auto",
            height: "auto",
            background: "none",
            float: "left",
            marginRight: "10px"
        },
        "& select": {
            height: "50px",
            padding: "0px 10px",
            borderRadius: "6px",
            border: "none",
            width: "100%",
            borderColor: colors.lightGrey,
            borderWidth: "1px",
            borderStyle: "solid"
        },
        "& .hs-submit": {
            "& input": {
                width: "100%",
                background: colors.blue,
                color: colors.white,
                border: "none",
                lineHeight: "36px",
                borderRadius: "6px",
                display: "block",
                cursor: "pointer"
            }
        },
        "& label": {
            display: "block",
            marginBottom: 5
        },
        "& label span.hs-form-required": {
            display: "none"
        },
        "& .hs_error_rollup": {
            "& label": {
                color: colors.red
            }
        },
        "& .input input.error": {
            outline: "auto 1px",
            outlineColor: colors.red,
            "&:focus": {
                outlineColor: colors.blue
            }
        },
        "& .input textarea.error": {
            outline: "auto 1px",
            outlineColor: colors.red,
            "&:focus": {
                outlineColor: colors.blue
            }
        },
        "& ul": {
            padding: 0,
            margin: "0 10px 0 0",
            "& label": {
                display: "block",
                fontSize: 12,
                marginTop: 4
            },
            "& li": {
                listStyle: "none"
            }
        },
        maxWidthTablet: {
            textAlign: "left"
        }
    }),
    "div",
    ["id"]
);
